import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Pill, Plus, Trash2 } from "lucide-react";

interface Medication {
  id?: string;
  medication_name: string;
  dosage: string;
  frequency: string;
  started_at: string;
}

interface MedicationsCardProps {
  medications: Medication[];
  onAdd: () => void;
  onRemove: (index: number) => void;
  onUpdate: (index: number, field: keyof Medication, value: string) => void;
}

export function MedicationsCard({ medications, onAdd, onRemove, onUpdate }: MedicationsCardProps) {
  return (
    <Card className="shadow-md border-0 bg-white/80 dark:bg-card backdrop-blur-sm">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
              <Pill className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <CardTitle className="text-lg">Médicaments</CardTitle>
              <CardDescription>{medications.length} médicament(s) en cours</CardDescription>
            </div>
          </div>
          <Button onClick={onAdd} variant="outline" size="sm" className="gap-2">
            <Plus className="w-4 h-4" />
            Ajouter
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {medications.length === 0 ? (
          <div className="text-center py-8 bg-muted/30 rounded-xl">
            <Pill className="w-10 h-10 mx-auto text-muted-foreground/40 mb-2" />
            <p className="text-muted-foreground text-sm">Aucun médicament enregistré</p>
          </div>
        ) : (
          <div className="space-y-3">
            {medications.map((med, index) => (
              <div key={index} className="p-4 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/20 dark:to-orange-950/20 rounded-xl border border-amber-100 dark:border-amber-900">
                <div className="flex justify-between items-start mb-3">
                  <Badge variant="outline" className="bg-white dark:bg-background">
                    Médicament {index + 1}
                  </Badge>
                  <Button variant="ghost" size="sm" onClick={() => onRemove(index)} className="h-8 w-8 p-0">
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </Button>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Nom</Label>
                    <Input
                      value={med.medication_name}
                      onChange={(e) => onUpdate(index, "medication_name", e.target.value)}
                      placeholder="Paracétamol"
                      className="h-9 bg-white dark:bg-background"
                    />
                  </div>

                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Dosage</Label>
                    <Input
                      value={med.dosage}
                      onChange={(e) => onUpdate(index, "dosage", e.target.value)}
                      placeholder="500mg"
                      className="h-9 bg-white dark:bg-background"
                    />
                  </div>

                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Fréquence</Label>
                    <Input
                      value={med.frequency}
                      onChange={(e) => onUpdate(index, "frequency", e.target.value)}
                      placeholder="2x/jour"
                      className="h-9 bg-white dark:bg-background"
                    />
                  </div>

                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Depuis le</Label>
                    <Input
                      type="date"
                      value={med.started_at}
                      onChange={(e) => onUpdate(index, "started_at", e.target.value)}
                      className="h-9 bg-white dark:bg-background"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
